import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Balder cart invariants — keeps cart lines coherent with the offer model:
 *
 * Selling plan follows quantity. A chew line on a subscription plan must
 * carry its size's 3-pack plan at quantity >= 3 and the 1-pack plan below
 * that, so pack pricing and refill cadence can't survive quantity edits
 * (e.g. a 3-pack decremented to 1 no longer keeps the -200 kr/jar price).
 *
 * Runs on page load and after every cart mutation. Client-side enforcement —
 * honest carts, not a security boundary.
 */

const SOURCE_ID = 'balder-cart-invariants';

// Must match BP_PLANS in blocks/bundle-picker.liquid.
// Also imported by balder-cart-drawer.js (subscription toggle).
export const PLANS = {
  liten: { 1: 738976792960, 3: 738976760192 },
  middels: { 1: 738976989568, 3: 738976956800 },
  stor: { 1: 738977055104, 3: 738977022336 },
};
export const CHEW_PRODUCT_IDS = [15816116535680, 15816120762752, 15816123679104];

export const PLAN_SIZE = {};
for (const [size, plans] of Object.entries(PLANS)) {
  PLAN_SIZE[plans[1]] = size;
  PLAN_SIZE[plans[3]] = size;
}

let running = false;

async function enforce() {
  if (running) return;
  running = true;
  try {
    // Each pass applies one fix and re-reads the cart; the invariants converge
    // in a pass or two — the bound only stops a runaway loop.
    for (let pass = 0; pass < 5; pass++) {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;
      const change = nextChange(await response.json());
      if (!change) return;
      try {
        await applyChange(change);
      } catch {
        // Usually a stale line key from a concurrent cart update — the next
        // pass re-reads the cart and recomputes.
      }
    }
  } catch (error) {
    console.error('[cart-invariants]', error);
  } finally {
    running = false;
  }
}

function nextChange(cart) {
  for (const item of cart.items) {
    if (!CHEW_PRODUCT_IDS.includes(item.product_id)) continue;
    const planId = item.selling_plan_allocation?.selling_plan?.id;
    const size = planId && PLAN_SIZE[planId];
    if (!size) continue;
    const expected = PLANS[size][item.quantity >= 3 ? 3 : 1];
    if (expected !== planId) {
      return { id: item.key, quantity: item.quantity, selling_plan: expected };
    }
  }

  return null;
}

async function applyChange(change) {
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(change) }));
  if (!response.ok) throw new Error(`cart change failed (${response.status})`);
  const cart = await response.json();
  // No sections payload — cart components re-render themselves on this event.
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE_ID, { source: SOURCE_ID, itemCount: cart.item_count }));
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SOURCE_ID) return;
  enforce();
});

enforce();
